import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Send, LifeBuoy, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Panel } from "@/components/PageHeader";
import { api, errMsg } from "@/lib/api";
import { StatusBadge, fmtDate } from "./ticketUi";
import { AttachButton, AttachmentList } from "./Attachments";
import { TicketRating } from "./TicketRating";

export function TicketDetail({ id, meta, onChange, onClose }) {
  const [t, setT] = useState(null);
  const [text, setText] = useState("");
  const [note, setNote] = useState("");
  const [assignee, setAssignee] = useState("");
  const [busy, setBusy] = useState("");
  const load = () => api.get(`/tickets/${id}`).then((r) => { setT(r.data); setAssignee(r.data.assignee || ""); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { setT(null); load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); setT(r.data); onChange(); if (ok) toast.success(ok); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const reply = () => act("reply", () => api.post(`/tickets/${id}/messages`, { text }), "Risposta inviata").then(() => setText(""));
  const setStatus = (status) => act(status, () => api.put(`/tickets/${id}/status`, { status, note }), `Stato: ${meta.statuses[status]}`).then(() => setNote(""));
  const closed = t?.status === "chiuso";

  return (
    <Panel testId="ticket-detail" title={t ? <span className="flex items-center gap-2"><span className="font-mono text-xs text-slate-400">#{t.number}</span>{t.subject}</span> : "Ticket"}
      action={<Button size="sm" variant="ghost" data-testid="ticket-close-panel" onClick={onClose}><X className="h-4 w-4" /></Button>}>
      {!t ? <Skeleton className="m-4 h-48" /> : (
        <div className="space-y-4 p-4">
          <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
            <StatusBadge status={t.status} meta={meta} testId="ticket-status-badge" />
            <span className="rounded bg-slate-100 px-1.5 py-0.5">{meta.categories[t.category]}</span>
            <span className="rounded bg-slate-100 px-1.5 py-0.5">Priorità {meta.priorities[t.priority]}</span>
            {meta.support && <span className="rounded bg-slate-100 px-1.5 py-0.5">{t.tenant_name}</span>}
            <span>Aperto da {t.created_by.email} il {fmtDate(t.created_at)}</span>{t.page && <span>· pagina {t.page}</span>}
          </div>
          <div className="max-h-[420px] space-y-3 overflow-auto" data-testid="ticket-messages">
            {t.messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === "support" ? "" : "flex-row-reverse"}`}>
                <div className={`mt-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${m.role === "support" ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-600"}`}>{m.role === "support" ? <LifeBuoy className="h-3.5 w-3.5" /> : <User className="h-3.5 w-3.5" />}</div>
                <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${m.role === "support" ? "bg-blue-50 text-slate-800" : "bg-slate-100 text-slate-800"}`}>
                  <div className="whitespace-pre-wrap">{m.text}</div>
                  <div className="mt-1 text-[10px] text-slate-400">{m.role === "support" ? "Assistenza" : m.by} · {fmtDate(m.at)}{m.status && ` · stato → ${meta.statuses[m.status]}`}</div>
                </div>
              </div>))}
          </div>
          <AttachmentList ticketId={id} attachments={t.attachments} />
          <TicketRating ticket={t} meta={meta} onRated={(nt) => { setT(nt); onChange(); }} />
          {!closed && (
            <div className="space-y-2 border-t border-slate-100 pt-3">
              <Textarea data-testid="ticket-reply-input" rows={3} value={text} onChange={(e) => setText(e.target.value)} placeholder={meta.support ? "Rispondi al cliente..." : "Aggiungi informazioni o rispondi all'assistenza..."} />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex gap-2">
                  <Button size="sm" data-testid="ticket-reply-send" className="bg-blue-600 hover:bg-blue-700" disabled={!text.trim() || !!busy} onClick={reply}><Send className="mr-1.5 h-3.5 w-3.5" />{busy === "reply" ? "Invio..." : "Invia"}</Button>
                  <AttachButton ticketId={id} disabled={!!busy} onUploaded={() => { load(); onChange(); }} />
                </div>
                {!meta.support && t.status !== "chiuso" && <Button size="sm" variant="outline" data-testid="ticket-user-close" disabled={!!busy} onClick={() => setStatus("chiuso")}>Problema risolto, chiudi</Button>}
              </div>
            </div>)}
          {meta.support && (
            <div className="space-y-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="ticket-support-tools">
              <div className="text-xs font-medium uppercase tracking-wider text-slate-500">Gestione assistenza</div>
              <Input data-testid="ticket-status-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Nota per il cliente (facoltativa, inviata via email)" className="text-sm" />
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(meta.statuses).map(([k, l]) => <Button key={k} size="sm" variant={t.status === k ? "default" : "outline"} data-testid={`ticket-set-status-${k}`} disabled={!!busy || t.status === k} onClick={() => setStatus(k)}>{busy === k ? "..." : l}</Button>)}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Input data-testid="ticket-assignee-input" value={assignee} onChange={(e) => setAssignee(e.target.value)} placeholder="Assegnato a (nome operatore)" className="w-60 text-sm" />
                <select data-testid="ticket-priority-select" value={t.priority} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm" onChange={(e) => act("prio", () => api.put(`/tickets/${id}/assign`, { assignee, priority: e.target.value }), "Priorità aggiornata")}>
                  {Object.entries(meta.priorities).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <Button size="sm" variant="outline" data-testid="ticket-assign-save" disabled={!!busy} onClick={() => act("assign", () => api.put(`/tickets/${id}/assign`, { assignee }), "Assegnazione salvata")}>Salva</Button>
              </div>
            </div>)}
          {closed && <p className="text-xs text-slate-500" data-testid="ticket-closed-note">Ticket chiuso. Per un nuovo problema apri una nuova richiesta.</p>}
        </div>)}
    </Panel>
  );
}
